"use client"

import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { BookingCta } from "@/components/ui/booking-cta"
import { Button } from "@/components/ui/button"
import { MapPinIcon } from "@/components/ui/icons"
import { useLocalePath } from "@/lib/use-localized-navigation"

export type LandingHeroProps = {
  headlineKey: string
  bodyKey: string
  primaryCtaKey: string
  primaryCtaTrackingLabel: string
  primaryCtaTrackingLocation: string
  secondaryCtaKey: string
  secondaryCtaHref: string
  locationKey: string
  imageSrc: string
  imageWidth: number
  imageHeight: number
  imageAlt?: string
  /** Controls headline size. "sm" → type-display-sm, "md" → type-display-md. Default "sm". */
  headlineSize?: "sm" | "md"
  /** Secondary CTA visual treatment. "arrow" adds "→"; "underline" uses text-underline; "button" renders the design-system outline button (matches homepage hero). Default "arrow". */
  secondaryCtaStyle?: "arrow" | "underline" | "button"
  /** Translation namespace for the keys. Default undefined (reads from root). */
  namespace?: string
}

export function LandingHero({
  headlineKey,
  bodyKey,
  primaryCtaKey,
  primaryCtaTrackingLabel,
  primaryCtaTrackingLocation,
  secondaryCtaKey,
  secondaryCtaHref,
  locationKey,
  imageSrc,
  imageWidth,
  imageHeight,
  imageAlt = "",
  headlineSize = "sm",
  secondaryCtaStyle = "arrow",
  namespace,
}: LandingHeroProps) {
  // Cast: next-intl strictly types the namespace arg against the generated
  // message tree; we accept arbitrary string here so the component can be
  // composed across namespaces by future LPs.
  const t = useTranslations(namespace as Parameters<typeof useTranslations>[0])
  const lp = useLocalePath()
  const headlineClass =
    headlineSize === "md" ? "type-display-md" : "type-display-sm"
  // Secondary CTA uses the type-title-sm DS token. Existing /go/* pages use an
  // off-system arbitrary px value here — Phase 2 migration aligns them to this
  // token. We never use ad-hoc px values for typography; see the rationale in
  // scripts/lint-typography.ts.
  const secondaryClass =
    secondaryCtaStyle === "underline"
      ? "inline-flex items-center gap-2 px-4 py-2 type-title-sm text-foreground underline underline-offset-2 transition-colors hover:text-aubergine-800"
      : "hover:text-aubergine-800 inline-flex items-center gap-2 px-4 py-2 type-title-sm text-foreground transition-colors"

  return (
    <section className="mx-auto max-w-[1274px] px-10 pt-20 pb-12 md:pt-28 md:pb-20">
      <div className="flex flex-col items-center gap-10 md:flex-row md:gap-16">
        <div className="flex-1 text-left">
          <h1 className={`${headlineClass} text-foreground leading-[0.9]`}>
            {t(headlineKey)}
          </h1>
          <p className="text-foreground mt-4 max-w-[460px] text-base leading-[1.3]">
            {t(bodyKey)}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <BookingCta
              trackingLabel={primaryCtaTrackingLabel}
              trackingLocation={primaryCtaTrackingLocation}
              className="bg-aubergine-800 inline-flex items-center justify-center rounded-full px-8 py-3 font-medium text-white transition-colors hover:bg-[#5a2a5d]"
            >
              {t(primaryCtaKey)}
            </BookingCta>
            {secondaryCtaStyle === "button" ? (
              <Link href={lp(secondaryCtaHref)}>
                <Button variant="outline">{t(secondaryCtaKey)}</Button>
              </Link>
            ) : (
              <Link href={lp(secondaryCtaHref)} className={secondaryClass}>
                {t(secondaryCtaKey)}
                {secondaryCtaStyle === "arrow" ? " \u2192" : ""}
              </Link>
            )}
          </div>
          <p className="mt-4 flex items-center gap-1.5 text-sm text-neutral-500">
            <MapPinIcon width={16} height={16} className="flex-shrink-0" />
            {t(locationKey)}
          </p>
        </div>
        <div className="w-full flex-shrink-0 md:w-[567px]">
          <Image
            src={imageSrc}
            alt={imageAlt}
            width={imageWidth}
            height={imageHeight}
            className="h-auto w-full rounded-[16px] object-cover"
            priority
          />
        </div>
      </div>
    </section>
  )
}
